import {Button, Paper} from '@material-ui/core';
import {Loaded, matchAsync} from 'Core/models/Async';
import {identity} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Lens} from 'monocle-ts';
import qs from 'query-string';
import React, {useEffect} from 'react';
import {connect, useSelector} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import Alert from 'Shared/components/Alert';
import ErrorPage from 'Shared/components/ErrorPage';
import Loading from 'Shared/components/Loading';
import RequiredTextInput from 'Shared/components/RequiredTextInput';
import {hasScope} from 'Shared/components/RequireScope';
import ValidatableEmailInput from 'Shared/components/ValidatableEmailInput';
import ValidatablePhoneInput from 'Shared/components/ValidatablePhoneInput';
import ValidatableTextInput from 'Shared/components/ValidatableTextInput';
import {Scope} from 'Shared/constants/Scope';
import {NoOp} from 'Shared/utilities/IOUtilities';
import {parseBoolean} from 'Shared/utilities/parsers';
import AgencyInput from 'Users/components/AgentAdministration/AgencyInput';
import * as AgentAdministrationActions from 'Users/ducks/agentAdministration';
import {
  AgentMapping,
  AgentMappingValue,
  eqAgentMapping,
  ErrorSavingAgentMapping,
  SavedAgentMapping,
  UnvalidatedAgentMapping,
  ValidAgentMapping,
} from 'Users/models/AgentMapping';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './AgentManualEntry.sass';
import AgencyAdministrationRouteConstants from 'AgencyAdministration/AgencyAdministrationRouteConstants';
import {ValidationError} from 'Users/ducks/independentAgent';
import {validateName} from './AgencyAdministratorAgentEntry/IndependentAgentEntry';

type AgentManualEntryProps = ReturnType<typeof mapStateToProps> &
  typeof AgentAdministrationActions &
  RouteComponentProps<{agentId: string}>;

const agentMappingValueLens = Lens.fromProp<AgentMapping>()('value');
const firstNameLens = Lens.fromProp<AgentMappingValue>()('firstName');
const lastNameLens = Lens.fromProp<AgentMappingValue>()('lastName');
const userNameLens = Lens.fromProp<AgentMappingValue>()('userName');
const emailLens = Lens.fromProp<AgentMappingValue>()('email');
const phoneLens = Lens.fromProp<AgentMappingValue>()('phoneNumber');
const agencyAssociationsLens = Lens.fromProp<AgentMappingValue>()('agencyAssociations');

export const AgentManualEntry = (props: AgentManualEntryProps) => {
  const editAgentId = O.fromNullable(props.match.params.agentId);
  const {selectedAgency} = useSelector((state: ReducerState) => state.user.profile);
  const userHasScope = hasScope(props.scopes);
  const isUserAgencyAdministrator = userHasScope(Scope.AgencyAdministrator);

  const changeMapping = (agentMapping: AgentMappingValue) => {
    props.changeAgentMapping(new Loaded(new UnvalidatedAgentMapping(agentMapping)));
  };
  useEffect(() => {
    pipe(
      editAgentId,
      O.fold(NoOp, agentId =>
        props.getAgent(agentId, O.isSome(selectedAgency) ? selectedAgency.value.encryptedIdentifier : undefined)
      )
    );

    return () => {
      props.clear();
      props.changeAgentErrors([]);
    };
  }, []);

  useEffect(() => {
    if (props.agentMapping instanceof SavedAgentMapping) {
      props.clearAgentMapping();
      props.history.goBack();
    }
  });

  const renderAgentForm = (
    agentMapping: AgentMapping,
    originalAgentMapping?: AgentMapping,
    errors?: ValidationError[]
  ) => {
    const hasAgentChanged =
      !originalAgentMapping || !eqAgentMapping.equals(agentMapping.value, originalAgentMapping.value);

    const isSupportUser =
      parseBoolean(qs.parse(props.location.search).isSupport as string) ||
      agentMapping.value.agencyAssociations.some(association => association.visibilityPermissions.length > 0);

    return (
      <Paper className="agent-entry">
        {agentMapping instanceof ErrorSavingAgentMapping && <Alert message={agentMapping.error} />}
        <h2 className="agent-entry__title">
          {pipe(
            editAgentId,
            O.fold(
              () => 'Add',
              () => 'Edit'
            )
          )}
          &nbsp;{isSupportUser ? 'Agent Support' : 'Agent'}
        </h2>
        <p className="agent-entry__description">Please enter the agent's information below to grant them access.</p>
        <form className="agent-entry__form">
          <RequiredTextInput
            label="First Name"
            className="agent-entry__field"
            disabled={isUserAgencyAdministrator}
            onChange={firstName =>
              changeMapping(agentMappingValueLens.composeLens(firstNameLens).set(firstName)(agentMapping).value)
            }
            value={agentMapping.value.firstName}
            additionalErrors={errors}
            changeError={props.changeAgentErrors}
            onValidate={validateName}
          />
          <RequiredTextInput
            label="Last Name"
            className="agent-entry__field"
            disabled={isUserAgencyAdministrator}
            onChange={lastName =>
              changeMapping(agentMappingValueLens.composeLens(lastNameLens).set(lastName)(agentMapping).value)
            }
            value={agentMapping.value.lastName}
            additionalErrors={errors}
            changeError={props.changeAgentErrors}
            onValidate={validateName}
          />
          <ValidatableTextInput
            label="User Name"
            className="agent-entry__field"
            disabled={O.isSome(editAgentId)}
            onValidate={props.validateUsername}
            value={agentMapping.value.userName}
          />
          <ValidatableEmailInput
            className="agent-entry__field"
            label="Email"
            disabled={isUserAgencyAdministrator}
            value={agentMapping.value.email}
            onValidate={props.validateEmail}
          />
          <ValidatablePhoneInput
            className="agent-entry__field"
            label="Mobile Phone"
            disabled={isUserAgencyAdministrator}
            value={agentMapping.value.phoneNumber}
            onValidate={props.validatePhone}
          />
          <AgencyInput
            isSupport={isSupportUser}
            scopes={props.scopes}
            agencyAssociations={agentMapping.value.agencyAssociations}
            changeAssociations={associations =>
              changeMapping(agencyAssociationsLens.set(associations)(agentMapping.value))
            }
          />
          <div className="agent-entry-form__buttons">
            <div className="agent-entry-form__return-button">
              <Button
                variant="contained"
                onClick={() => {
                  props.history.goBack();
                }}
              >
                Cancel
              </Button>
            </div>
            <Button
              className="agent-entry-form__add-button"
              color="primary"
              variant="contained"
              disabled={
                !(agentMapping instanceof ValidAgentMapping) || !hasAgentChanged || (errors && errors.length > 0)
              }
              onClick={() => {
                props.createAgentMapping(
                  agentMapping as ValidAgentMapping,
                  isSupportUser,
                  pipe(
                    editAgentId,
                    O.fold(() => '', identity)
                  )
                );
              }}
            >
              Save
            </Button>
          </div>
        </form>
      </Paper>
    );
  };

  return matchAsync(props.agentMapping, {
    Loaded: agentMapping => {
      return matchAsync(props.originalAgentMapping, {
        Loaded: originalAgentMapping => renderAgentForm(agentMapping, originalAgentMapping, props.errors),
        Loading: () => renderAgentForm(agentMapping),
        ServerError: error => <ErrorPage error={error} />,
        Unauthorized: error => <ErrorPage error={error} />,
        Conflict: error => <ErrorPage error={error} />,
      });
    },
    Loading: () => <Loading />,
    ServerError: error => <ErrorPage error={error} />,
    Unauthorized: error => <ErrorPage error={error} />,
    Conflict: error => <ErrorPage error={error} />,
  });
};

const mapStateToProps = (state: ReducerState) => ({
  agentMapping: state.user.agentAdministration.agentMapping,
  originalAgentMapping: state.user.agentAdministration.originalAgentMapping,
  userClaims: state.user.profile.userClaims,
  scopes: selectUserScopes(state),
  errors: state.user.agentAdministration.validationErrors,
});

export default withRouter(connect(mapStateToProps, {...AgentAdministrationActions})(AgentManualEntry));
